import { useState, useEffect, useCallback } from "react";
import {
  BookMarked,
  Save,
  Loader2,
  Check,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { apiv2, type EngagementInfo } from "../apiv2";

interface Props {
  projectId: string;
  canEdit: boolean;
}

/**
 * Binds a project to its engagement KB (`engagement_slug`) and optionally
 * narrows which `dq-rules.*` packs the profiler loads (`dq_rule_packs`).
 * Persists to project.config via PUT /api/v2/projects/{id}/engagement-config.
 * Previously this was only settable via manual SQL on projects.config.
 */
export default function EngagementConfig({ projectId, canEdit }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const [engagements, setEngagements] = useState<EngagementInfo[]>([]);
  const [slug, setSlug] = useState("");
  // Selected packs. Empty set = "all packs under the engagement".
  const [packs, setPacks] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, cfg] = await Promise.all([
        apiv2.listEngagements().catch(() => [] as EngagementInfo[]),
        apiv2.getEngagementConfig(projectId).catch(() => null),
      ]);
      setEngagements(list);
      if (cfg) {
        setSlug(cfg.engagement_slug || "");
        setPacks(new Set(cfg.dq_rule_packs || []));
      }
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  // Packs available for the currently-selected engagement.
  const availablePacks = engagements.find((e) => e.slug === slug)?.packs ?? [];

  const togglePack = (name: string) => {
    if (!canEdit) return;
    setPacks((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      // Only persist packs that belong to the selected engagement.
      const selected = availablePacks.filter((p) => packs.has(p));
      const result = await apiv2.setEngagementConfig(projectId, {
        engagement_slug: slug,
        dq_rule_packs: selected,
      });
      setSlug(result.engagement_slug || "");
      setPacks(new Set(result.dq_rule_packs || []));
      setSuccess("Saved successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="glass rounded-2xl overflow-hidden"
      data-testid="engagement-config-section"
      style={{
        background: "rgba(28,28,42,0.5)",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.02] transition"
        data-testid="engagement-config-toggle"
      >
        <BookMarked size={16} className="text-amber-400" />
        <span className="text-sm font-bold text-white flex-1">
          Engagement &amp; DQ Rule Packs
        </span>
        {slug && (
          <span
            className="text-[11px] text-green-400 bg-green-500/10 px-2 py-0.5 rounded border border-green-500/20"
            data-testid="engagement-config-badge"
          >
            {slug}
          </span>
        )}
        {open ? (
          <ChevronUp size={14} className="text-white/30" />
        ) : (
          <ChevronDown size={14} className="text-white/30" />
        )}
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 size={20} className="animate-spin text-white/20" />
            </div>
          ) : (
            <>
              <p className="text-xs text-white/40">
                Bind this project to an engagement knowledge base. The profiler
                loads that engagement&apos;s business-rule packs at run time.
                Leave all packs unchecked to load every pack under the
                engagement.
              </p>

              {/* Engagement slug */}
              <div>
                <label className="block text-xs text-white/40 mb-1">
                  Engagement
                </label>
                <select
                  value={slug}
                  onChange={(e) => {
                    setSlug(e.target.value);
                    setPacks(new Set()); // reset packs when engagement changes
                  }}
                  disabled={!canEdit}
                  data-testid="engagement-slug-select"
                  className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50 disabled:opacity-50"
                >
                  <option value="">— None —</option>
                  {/* Preserve a current slug that isn't on disk, so it round-trips. */}
                  {slug && !engagements.some((e) => e.slug === slug) && (
                    <option value={slug}>{slug} (not found on disk)</option>
                  )}
                  {engagements.map((e) => (
                    <option key={e.slug} value={e.slug}>
                      {e.slug}
                    </option>
                  ))}
                </select>
              </div>

              {/* DQ rule packs */}
              {slug && (
                <div>
                  <label className="block text-xs text-white/40 mb-2">
                    DQ Rule Packs{" "}
                    <span className="text-white/25">
                      ({availablePacks.length} available
                      {packs.size === 0 ? " — all load when none selected" : ""}
                      )
                    </span>
                  </label>
                  {availablePacks.length === 0 ? (
                    <p className="text-xs text-white/25 italic">
                      No dq-rules.* packs found for this engagement.
                    </p>
                  ) : (
                    <div className="space-y-1.5" data-testid="engagement-packs">
                      {availablePacks.map((name) => (
                        <label
                          key={name}
                          className="flex items-center gap-2 text-sm text-white/70 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={packs.has(name)}
                            onChange={() => togglePack(name)}
                            disabled={!canEdit}
                            data-testid="engagement-pack-checkbox"
                            data-pack={name}
                            className="accent-brand-500"
                          />
                          <span className="font-mono text-xs">{name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-xs text-red-400">
                  <AlertTriangle size={13} /> {error}
                </div>
              )}
              {success && (
                <div
                  className="flex items-center gap-2 text-xs text-green-400"
                  data-testid="engagement-config-success"
                >
                  <Check size={13} /> {success}
                </div>
              )}

              {canEdit && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="engagement-config-save"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                  style={{
                    background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                  }}
                >
                  {saving ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Save size={14} />
                  )}
                  Save
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
